import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTutorAvailabilityStore } from "src/store/tutorAvailabilityStore";
import { useTutorPreferencesUIStore } from "src/store/tutorPreferencesUIStore";
import { useTimezone } from "src/hooks/useTimezone";
import { useAuth } from "src/context/AuthContext";
import { useToast } from "src/components/ui/UseToast";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/Input";
import { Label } from "src/components/ui/Label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "src/components/ui/Card";
import { Clock, Moon, Timer, Globe, ExternalLink } from "lucide-react";
import { Badge } from "src/components/ui/Badge";
import { ROUTES } from "src/routes/RouteConfig";

const TutorPreferences: React.FC = () => {
  const navigate = useNavigate();
  const { preferences, updatePreferences } = useTutorAvailabilityStore();
  const { user, profileData } = useAuth();
  const { toast } = useToast();
  const { getTimezoneDisplayInfo, currentTimezone } = useTimezone();

  // Zustand store for UI state
  const {
    isEditing,
    editedPreferences,
    newDuration,
    setIsEditing,
    setEditedPreferences,
    setNewDuration,
    initializePreferences,
    cancelEditing,
  } = useTutorPreferencesUIStore();

  // Track if initialization has happened
  const isInitialized = useRef(false);

  // Initialize preferences on component mount
  useEffect(() => {
    if (!isInitialized.current && preferences) {
      initializePreferences(preferences);
      isInitialized.current = true;
    }
  }, [preferences, initializePreferences]);

  // Handle input changes
  const handleInputChange = (field: keyof typeof editedPreferences, value: any) => {
    setEditedPreferences({
      ...editedPreferences,
      [field]: value,
    });
  };

  // Handle nested object changes
  const handleNestedChange = (
    parent: keyof typeof editedPreferences,
    field: string,
    value: any
  ) => {
    const parentValue = editedPreferences[parent];

    // Ensure the parent value is an object before spreading
    if (typeof parentValue === 'object' && parentValue !== null) {
      setEditedPreferences({
        ...editedPreferences,
        [parent]: {
          ...parentValue,
          [field]: value,
        },
      });
    }
  };

  // Add a new session duration
  const handleAddDuration = () => {
    const duration = parseInt(newDuration);
    if (
      duration > 0 &&
      !editedPreferences.sessionDurations.includes(duration)
    ) {
      setEditedPreferences({
        ...editedPreferences,
        sessionDurations: [...editedPreferences.sessionDurations, duration].sort(
          (a, b) => a - b
        ),
      });
      setNewDuration("");
    }
  };

  // Remove a session duration
  const handleRemoveDuration = (duration: number) => {
    setEditedPreferences({
      ...editedPreferences,
      sessionDurations: editedPreferences.sessionDurations.filter(
        (d) => d !== duration
      ),
    });
  };

  // Save changes
  const handleSaveChanges = () => {
    updatePreferences(editedPreferences);
    setIsEditing(false);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    cancelEditing(preferences);
  };

  // Navigate to Account Preferences for timezone settings
  const handleTimezoneSettings = () => {
    navigate(ROUTES.TUTOR_ACCOUNT_PREFERENCES.path);
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Tutor Preferences</h2>
        {!isEditing ? (
          <Button onClick={() => setIsEditing(true)}>Edit Preferences</Button>
        ) : (
          <div className="space-x-2">
            <Button variant="outline" onClick={handleCancelEdit}>
              Cancel
            </Button>
            <Button onClick={handleSaveChanges}>Save Changes</Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Working Hours */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-500" />
              Default Working Hours
            </CardTitle>
            <CardDescription>
              Set your standard working hours for availability
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={editedPreferences.defaultWorkingHours.startTime}
                      onChange={(e) =>
                        handleNestedChange(
                          "defaultWorkingHours",
                          "startTime",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={editedPreferences.defaultWorkingHours.endTime}
                      onChange={(e) =>
                        handleNestedChange(
                          "defaultWorkingHours",
                          "endTime",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-2">
                <p className="text-lg font-medium">
                  {preferences.defaultWorkingHours.startTime} - {preferences.defaultWorkingHours.endTime}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  These hours will be used as defaults when creating new availability slots
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Session Durations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Timer className="h-5 w-5 mr-2 text-blue-500" />
              Session Duration Presets
            </CardTitle>
            <CardDescription>
              Define standard session lengths that students can book
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {editedPreferences.sessionDurations.map((duration) => (
                    <Badge key={duration} variant="secondary">
                      {duration} min
                      <button
                        className="ml-1 text-gray-500 hover:text-red-500"
                        onClick={() => handleRemoveDuration(duration)}
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex space-x-2">
                  <Input
                    type="number"
                    placeholder="Duration in minutes"
                    value={newDuration}
                    onChange={(e) => setNewDuration(e.target.value)}
                    min="5"
                    step="5"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleAddDuration}
                  >
                    Add
                  </Button>
                </div>
              </div>
            ) : (
              <div className="py-2">
                <div className="flex flex-wrap gap-2">
                  {preferences.sessionDurations.map((duration) => (
                    <Badge key={duration} variant="secondary">
                      {duration} min
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Buffer Time */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-500" />
              Buffer Time Between Sessions
            </CardTitle>
            <CardDescription>
              Set a break period between consecutive sessions
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Buffer Time (minutes)</Label>
                  <Input
                    type="number"
                    value={editedPreferences.bufferTime}
                    onChange={(e) =>
                      handleInputChange("bufferTime", parseInt(e.target.value))
                    }
                    min="0"
                    step="5"
                  />
                </div>
              </div>
            ) : (
              <div className="py-2">
                <p className="text-lg font-medium">{preferences.bufferTime} minutes</p>
                <p className="text-sm text-gray-500 mt-1">
                  This time will be automatically blocked between sessions
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Do Not Disturb */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Moon className="h-5 w-5 mr-2 text-blue-500" />
              Do-Not-Disturb Hours
            </CardTitle>
            <CardDescription>
              Set hours when you don't want to be booked
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isEditing ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={editedPreferences.doNotDisturbHours.startTime}
                      onChange={(e) =>
                        handleNestedChange(
                          "doNotDisturbHours",
                          "startTime",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={editedPreferences.doNotDisturbHours.endTime}
                      onChange={(e) =>
                        handleNestedChange(
                          "doNotDisturbHours",
                          "endTime",
                          e.target.value
                        )
                      }
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div className="py-2">
                <p className="text-lg font-medium">
                  {preferences.doNotDisturbHours.startTime} - {preferences.doNotDisturbHours.endTime}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  No sessions will be scheduled during these hours
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timezone - Link to Account Preferences */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Globe className="h-5 w-5 mr-2 text-blue-500" />
              Time Zone
            </CardTitle>
            <CardDescription>
              Manage your timezone preferences for accurate scheduling
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="py-2">
              <p className="text-lg font-medium">
                {currentTimezone ? getTimezoneDisplayInfo(currentTimezone).label : 'Not set'}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                All times are displayed in your local time zone
              </p>
              <div className="mt-4">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTimezoneSettings}
                  className="flex items-center gap-2"
                >
                  <ExternalLink className="h-4 w-4" />
                  Manage Timezone Settings
                </Button>
                <p className="text-xs text-gray-500 mt-2">
                  Configure timezone preferences, auto-detection, and advanced settings in Account Preferences
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TutorPreferences;